import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

type ServicesGreenEnergyProps = {
  onBack: () => void;
};

export default function ServicesGreenEnergy({ onBack }: ServicesGreenEnergyProps): React.ReactElement {
  return (
    <section className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
              Service Two
            </span>
            <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
              Green Energy Solutions (Solar PV)
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
              In an era of energy uncertainty, Ancient Engineering provides robust, engineered solar solutions
              that guarantee energy security and significant ROI.
            </p>
          </div>

          {/* Sector-Specific Solar Engineering */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              Sector-Specific Solar Engineering
            </h3>
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Residential Systems */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <HomeOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Residential Systems</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  From small backup kits to large-scale off-grid estates, we design systems that grow with your needs.
                </p>
              </div>

              {/* Commercial & Industrial */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <BusinessOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Commercial & Industrial</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  We help factories and office complexes reduce operational overhead through grid-tied and hybrid solar
                  solutions that handle heavy industrial loads.
                </p>
              </div>

              {/* Agriculture & Utility */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <AgricultureOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Agriculture & Utility</h4>
                </div>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Solar solutions for irrigation, agricultural buildings, and cold storage.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Large-scale solar field development.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>
                      <strong className="font-semibold text-slate-900">Financial Support:</strong> Guidance on finance
                      options for large-scale agricultural energy projects.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* End-to-End Project Management */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              End-to-End Project Management
            </h3>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <EngineeringOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Balance of System (BoS) Design</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  Engineering the racks, wiring, and protection systems for maximum longevity.
                </p>
              </div>

              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <VerifiedOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Professional Commissioning</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  Rigorous testing to ensure the system meets all safety and performance benchmarks before handover.
                </p>
              </div>
            </div>
          </div>

          {/* Why Choose Our Solar */}
          <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <SolarPowerOutlinedIcon className="text-white" fontSize="large" />
              <h4 className="text-2xl font-semibold text-white">Why Choose Our Solar?</h4>
            </div>
            <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
              We don't just install panels; we engineer a power plant. Our systems are designed using high-grade
              components like Victron and Phocos to ensure a 25-year lifespan.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


